import {
  isPlanBasic,
  isPlanEnterprise,
  planTiers,
  tiersHierarchy,
} from "@/react/helpers/planHelpers";
import { useEnterpriseOfferEnabled } from "@/react/hooks/useEnterpriseOfferEnabled";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import { usePlans } from "@circle-react/components/SettingsApp/Plan/RegularPlan/PlansContext";
import { PlansPageBanner } from "@circle-react/components/shared/PlusUpgradeBanners/PlansPageBanner";
import { usePunditUserContext } from "@circle-react/contexts";
import { CurrentPlan } from "./CurrentPlan";
import { EnterprisePriceIncrease } from "./EnterprisePriceIncrease";
import { ExpiredTrial } from "./ExpiredTrial";
import { GrandfatheredDowngradeWarning } from "./GrandfatheredDowngradeWarning";
import { MarketingCampaignBanner } from "./MarketingCampaignBanner";
import { SpcBanner } from "./SpcBanner";
import { SpecialOffer } from "./SpecialOffer";
import { SubscriptionCancelled } from "./SubscriptionCancelled";
import { SubscriptionIncompleteExpired } from "./SubscriptionIncompleteExpired";
import { SubscriptionOnGracePeriod } from "./SubscriptionOnGracePeriod";
import { SubscriptionWithIncompletePayment } from "./SubscriptionWithIncompletePayment";

export const TopSection = () => {
  const { currentPlan, plans } = usePlans();
  const { currentCommunity } = usePunditUserContext();
  const { isEnterpriseOfferEnabled } = useEnterpriseOfferEnabled();
  const {
    isExpiredTrial,
    isCommunityOnTrial,
    isCommunitySubscriptionIncompleteExpired,
    isCommunitySubscriptionFullyCancelled,
    isCommunitySubscriptionOnGracePeriod,
    hasCommunityIncompletePayment,
    isPromoCodeEnabledForCommunity,
    hasActiveOffer,
  } = usePlanStatus();

  const shouldRenderCurrentPlan = Boolean(
    !isCommunityOnTrial &&
      !isExpiredTrial &&
      currentCommunity?.subscription &&
      !isPlanBasic(currentPlan.tier) &&
      !isCommunitySubscriptionIncompleteExpired &&
      !isCommunitySubscriptionFullyCancelled,
  );

  const shouldShowEnterprisePriceIncrease =
    tiersHierarchy[currentPlan.tier] < tiersHierarchy[planTiers.enterprise] &&
    isEnterpriseOfferEnabled;

  const getSubscriptionStatusBanner = () => {
    if (isExpiredTrial) {
      return <ExpiredTrial />;
    }

    if (
      isCommunityOnTrial &&
      !isPromoCodeEnabledForCommunity &&
      !hasActiveOffer &&
      !shouldShowEnterprisePriceIncrease
    ) {
      return <SpecialOffer />;
    }

    if (hasCommunityIncompletePayment) {
      return <SubscriptionWithIncompletePayment />;
    }

    if (isCommunitySubscriptionIncompleteExpired) {
      return <SubscriptionIncompleteExpired />;
    }

    if (isCommunitySubscriptionFullyCancelled) {
      return <SubscriptionCancelled />;
    }

    if (isCommunitySubscriptionOnGracePeriod) {
      return <SubscriptionOnGracePeriod />;
    }

    if (isPromoCodeEnabledForCommunity && !shouldRenderCurrentPlan) {
      return <SpcBanner />;
    }

    return null;
  };

  const marketingCampaignBanner = hasActiveOffer ? (
    <MarketingCampaignBanner
      shouldRenderCurrentPlan={shouldRenderCurrentPlan}
    />
  ) : null;

  const subscriptionStatusBanner = getSubscriptionStatusBanner();

  const shouldRenderGrandFatheredBanner =
    (currentPlan.show_pro_grandfathered_for_courses ||
      (isPlanEnterprise(currentPlan.tier) && !currentPlan.public) ||
      isPlanBasic(currentPlan.tier)) &&
    !isExpiredTrial &&
    !isCommunityOnTrial;

  const enterprisePlan = plans.find(plan => plan.tier === planTiers.enterprise);

  return (
    <div className="grid gap-10">
      {shouldRenderGrandFatheredBanner && (
        <GrandfatheredDowngradeWarning currentPlan={currentPlan} />
      )}
      <PlansPageBanner />
      {subscriptionStatusBanner}
      {marketingCampaignBanner}
      {!isCommunityOnTrial && shouldShowEnterprisePriceIncrease && (
        <EnterprisePriceIncrease planId={enterprisePlan?.id} />
      )}
      {shouldRenderCurrentPlan && <CurrentPlan />}
      <hr className="mb-4 h-px border-0 bg-gray-200 dark:bg-gray-700" />
    </div>
  );
};
